import { useResolvedAccessLevel } from '../../components/PermissionsContainer';

/**
 * Component id for the main-invoice Bill From gate.
 *
 * Bill From (`invoice.billfromsystem`) rode `order-lc-fields` from the port until now —
 * the same grant that opens Port of Origin / Discharge / DC / Dept and the Invoice Hold
 * group. That welded "may decide an order is not billed" to "may edit LC order fields",
 * which are different jobs with different audiences: the invoicing desk owns the first
 * and holds none of the second, while every Logistic Coordinator picked up the first for
 * free. Same split, same reasoning, as `invoice-approval` (useCanApproveInvoice) and
 * `invoice-do-not-bill-reason` — the reason dropdown this field reveals already has its
 * own gate, so the field that reveals it riding a broader one was the odd part.
 *
 * STANDALONE, not ANDed with the LC gate. The call site passes this INSTEAD of
 * `holdEditable`, so a user who holds Bill From and only View on `order-lc-fields` can
 * edit it — which is the point: GWI360-InvoiceManager is View on the LC fields by
 * default and must still be able to change Bill From. Levels resolve highest-wins per
 * component and independently (resolve_component_access_levels), so a View grant on one
 * component cannot suppress an Edit grant on another.
 *
 * ORDER-SIDE ONLY. On a TEMPLATE the template-edit permission is the sole authority
 * (templateFieldPolicy billFromSystem = State 2, editable while unapproved) and this
 * component has no say — `fieldEditable` short-circuits to the template arm, the same
 * carve-out InvoiceWriteGate and OrderLcFieldGroup make.
 *
 * The INVOICED lock is unaffected and still applies on top: order_field_display_rules
 * row 261 (`billfromsystem` / INVOICED / readonly / overridable) takes the field
 * read-only once the invoice has an invoicedate, for this audience too, and only Admin
 * Override releases it.
 */
export const BILL_FROM_COMPONENT_ID = 'invoice-bill-from';
export const BILL_FROM_COMPONENT_NAME = 'Pro Invoicing — Bill From';

/**
 * Whether the current user may change Bill From on an ORDER's main invoice.
 *
 * Resolved from the admin-configured component permission crossed with the user's Azure
 * AD groups. Ships **defaultDisabled**: with no grant the dropdown renders as read-only
 * text, so the stored value stays visible to everyone who can open the tab — nobody
 * loses sight of whether an order bills, only the ability to change it.
 *
 * >>> Migration 505 is not optional, and must land with the deploy, not after. <<<
 * It seeds this component from whatever holds `order-lc-fields` >= 2 in that schema,
 * plus GWI360-InvoiceManager. On a schema where it has not run, defaultDisabled means
 * Bill From goes read-only for EVERY user — including the 100 who can change it today.
 */
export const useCanEditBillFrom = (): boolean =>
  useResolvedAccessLevel(BILL_FROM_COMPONENT_ID, false, true) >= 2;
